import ConfigManager from "./ConfigManager.js";

/**
 * Extrait le PBO depuis l'ID câble
 * @param {string} idCable - ID câble complet
 * @returns {string} PBO (partie avant _B2-)
 *
 * Exemples :
 * BE-SC-121928345-01_B2-T1-ME → BE-SC-121928345-01
 * PBO007_B2-T3-ME → PBO007
 */
function extractPBO(idCable) {
  return idCable.split("_B2-")[0];
}

/**
 * Analyse les doublons dans les données de fibres
 * @param {Array} fibers - Tableau des fibres avec moyennes et atténuations
 * @returns {Object} Résultats de l'analyse avec paires regroupées par PBO
 */
export function analyzeDuplicates(fibers) {
  validateInput(fibers);

  // Récupérer la config active
  const config = ConfigManager.getConfig();

  // Étape 1: Enrichir chaque fibre avec son PBO
  fibers.forEach((fiber) => {
    fiber.pbo = extractPBO(fiber.idCable);
  });

  // Étape 2: Créer groupes par (PBO + Lambda)
  const groups = createGroups(fibers);

  // Étape 3: Analyser chaque groupe et construire les paires
  const allPairs = [];
  let totalComparisons = 0;

  groups.forEach((groupFibers, groupKey) => {
    if (groupFibers.length < 2) return;

    const groupResult = analyzeGroup(groupFibers, config);
    allPairs.push(...groupResult.pairs);
    totalComparisons += groupResult.comparisons;
  });

  // Étape 4: Enrichir les paires avec métadonnées et trier
  const enrichedPairs = allPairs.map((pair, index) => enrichPairForDisplay(pair, index + 1));
  const sortedPairs = enrichedPairs.sort(comparePairsPriority);

  return {
    totalFibers: fibers.length,
    totalComparisons,
    clustersFound: sortedPairs.length,
    clusters: sortedPairs, // ClusterCard s'attend à cette clé
    summary: createSummary(fibers, sortedPairs),
  };
}

/**
 * Valide les données d'entrée
 */
function validateInput(fibers) {
  if (!Array.isArray(fibers)) {
    throw new Error("Le paramètre fibers doit être un tableau");
  }

  if (fibers.length === 0) {
    throw new Error("Le tableau de fibres ne peut pas être vide");
  }

  const requiredFields = [
    "idCable",
    "numFibre",
    "lambda",
    "attenuations",
    "moyenneConnecteurs",
    "moyenneEpissures",
    "perteTotale",
  ];

  const invalidFiber = fibers.find((fiber) => requiredFields.some((field) => fiber[field] === undefined));

  if (invalidFiber) {
    throw new Error(
      `Fibre invalide trouvée (données manquantes): ${invalidFiber.idCable} - Fibre ${invalidFiber.numFibre}`
    );
  }
}

/**
 * Crée des groupes de fibres par (PBO + Lambda)
 */
function createGroups(fibers) {
  const groups = new Map();

  fibers.forEach((fiber) => {
    const groupKey = `${fiber.pbo}_${fiber.lambda}`;

    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }

    groups.get(groupKey).push(fiber);
  });

  return groups;
}

/**
 * Analyse un groupe de fibres compatibles et construit les paires suspectes
 * Logique de matching : AU MOINS 1 des 3 moyennes doit respecter son seuil (logique OU)
 */
function analyzeGroup(groupFibers, config) {
  const pairs = [];
  let comparisons = 0;

  // Comparer toutes les paires de fibres
  for (let i = 0; i < groupFibers.length - 1; i++) {
    for (let j = i + 1; j < groupFibers.length; j++) {
      const fiber1 = groupFibers[i];
      const fiber2 = groupFibers[j];

      // Exclure comparaison même fibre (même idCable + numFibre)
      if (fiber1.idCable === fiber2.idCable && fiber1.numFibre === fiber2.numFibre) {
        continue;
      }

      const comparison = compareFibers(fiber1, fiber2, config);
      comparisons++;

      // Si AU MOINS 1 critère match, ajouter aux paires
      if (comparison.isSuspicious) {
        pairs.push({
          pbo: fiber1.pbo,
          lambda: fiber1.lambda,
          fiber1,
          fiber2,
          matchingCriteria: comparison.matchingCriteria,
          differences: comparison.differences,
        });
      }
    }
  }

  return { pairs, comparisons };
}

/**
 * Compare deux fibres sur les 3 moyennes
 * Logique OU : au moins 1 des 3 moyennes doit être sous son seuil
 */
function compareFibers(fiber1, fiber2, config) {
  // Calculer les 3 différences
  const diffConn = Math.abs(fiber1.moyenneConnecteurs - fiber2.moyenneConnecteurs);
  const diffEpis = Math.abs(fiber1.moyenneEpissures - fiber2.moyenneEpissures);
  const diffPerte = Math.abs(fiber1.perteTotale - fiber2.perteTotale);

  // Vérifier chaque critère
  const matchConn = diffConn <= config.seuilMoyenneConnecteurs;
  const matchEpis = diffEpis <= config.seuilMoyenneEpissures;
  const matchPerte = diffPerte <= config.seuilPerteTotale;

  // Logique OU : au moins 1 doit matcher
  const isSuspicious = matchConn || matchEpis || matchPerte;

  // Compter combien de critères matchent
  const matchingCriteria = [matchConn, matchEpis, matchPerte].filter(Boolean).length;

  return {
    isSuspicious,
    matchingCriteria,
    differences: {
      moyenneConnecteurs: { value: diffConn, match: matchConn },
      moyenneEpissures: { value: diffEpis, match: matchEpis },
      perteTotale: { value: diffPerte, match: matchPerte },
    },
  };
}

/**
 * Enrichit une paire avec métadonnées pour l'affichage dans ClusterCard
 */
function enrichPairForDisplay(pair, id) {
  const fiber1Atts = pair.fiber1.attenuations;
  const fiber2Atts = pair.fiber2.attenuations;

  // Adapter les fibres pour ClusterCard avec les 3 moyennes
  const adaptedFibers = [
    {
      idCable: pair.fiber1.idCable,
      numFibre: pair.fiber1.numFibre,
      lambda: pair.fiber1.lambda,
      couleur: pair.fiber1.couleur,
      attenuations: fiber1Atts,
      moyenneConnecteurs: pair.fiber1.moyenneConnecteurs,
      moyenneEpissures: pair.fiber1.moyenneEpissures,
      perteTotale: pair.fiber1.perteTotale,
    },
    {
      idCable: pair.fiber2.idCable,
      numFibre: pair.fiber2.numFibre,
      lambda: pair.fiber2.lambda,
      couleur: pair.fiber2.couleur,
      attenuations: fiber2Atts,
      moyenneConnecteurs: pair.fiber2.moyenneConnecteurs,
      moyenneEpissures: pair.fiber2.moyenneEpissures,
      perteTotale: pair.fiber2.perteTotale,
    },
  ];

  return {
    id,
    pbo: pair.pbo,
    lambda: pair.lambda,
    fibers: adaptedFibers,
    fiberCount: 2,
    matchingCriteria: pair.matchingCriteria,
    differences: pair.differences,
  };
}

/**
 * Compare deux paires pour le tri
 */
function comparePairsPriority(pairA, pairB) {
  // 1. Par PBO
  if (pairA.pbo !== pairB.pbo) {
    return pairA.pbo.localeCompare(pairB.pbo);
  }

  // 2. Par longueur d'onde
  if (pairA.lambda !== pairB.lambda) {
    return pairA.lambda.localeCompare(pairB.lambda);
  }

  // 3. Par nombre de critères qui matchent (décroissant)
  if (pairA.matchingCriteria !== pairB.matchingCriteria) {
    return pairB.matchingCriteria - pairA.matchingCriteria;
  }

  // 4. Par ID câble
  return pairA.fibers[0].idCable.localeCompare(pairB.fibers[0].idCable);
}

/**
 * Crée le résumé de l'analyse
 */
function createSummary(allFibers, pairs) {
  const fibersInPairs = new Set();

  pairs.forEach((pair) => {
    pair.fibers.forEach((fiber) => {
      fibersInPairs.add(`${fiber.idCable}|${fiber.numFibre}|${fiber.lambda}`);
    });
  });

  const wavelengthStats = {};
  const pboStats = {};

  allFibers.forEach((fiber) => {
    // Stats par longueur d'onde
    if (!wavelengthStats[fiber.lambda]) {
      wavelengthStats[fiber.lambda] = { total: 0, inClusters: 0 };
    }
    wavelengthStats[fiber.lambda].total++;

    const fiberKey = `${fiber.idCable}|${fiber.numFibre}|${fiber.lambda}`;
    if (fibersInPairs.has(fiberKey)) {
      wavelengthStats[fiber.lambda].inClusters++;
    }

    // Stats par PBO
    if (!pboStats[fiber.pbo]) {
      pboStats[fiber.pbo] = { total: 0, inPairs: 0 };
    }
    pboStats[fiber.pbo].total++;
    if (fibersInPairs.has(fiberKey)) {
      pboStats[fiber.pbo].inPairs++;
    }
  });

  return {
    totalFibers: allFibers.length,
    fibersInClusters: fibersInPairs.size,
    fibersWithoutIssues: allFibers.length - fibersInPairs.size,
    wavelengthStats,
    pboStats,
  };
}
